import { FC, Suspense, useCallback } from 'react';
import { ErrorMessage } from '../../components/errors/ErrorMessage';
import { strings } from '../../services/translation/strings';
import { WorkingShift } from '../../types/daily.types';
import { WorkingRoster } from '../../types/roster.types';
import { RosterShift } from './RosterShift';

interface Props {
  workingRoster: WorkingRoster;
  setWorkingRoster: (newRoster: WorkingRoster) => void;
}

export const RosterShifts: FC<Props> = ({ workingRoster, setWorkingRoster }) => {
  const updateShift = useCallback(
    (shiftIndex: number) => (newShift: WorkingShift) => {
      if (!workingRoster) {
        throw Error('Working Roster should be set');
      }

      // updating working roster re-renders lots of components, and too expensive.
      // Deep comparison must be faster and avoids activating the Save button unnecessarily.
      const workingShiftAsString = JSON.stringify(workingRoster.shifts[shiftIndex]);
      const newShiftAsString = JSON.stringify(newShift);
      if (workingShiftAsString === newShiftAsString) {
        return;
      }

      const shifts = [...workingRoster.shifts];
      const workingRosterShift = workingRoster.shifts.find(s => s.id === newShift.id);

      if (workingRosterShift) {
        shifts.splice(shiftIndex, 1, {
          ...workingRosterShift,
          breaks: newShift.breaks,
          tasks: newShift.tasks,
          start: newShift.start,
          end: newShift.end,
          properties: newShift.properties,
          assignedEmployeeId: newShift.assignedEmployeeId,
          assignedContractId: newShift.assignedContractId,
          assignedStaffHomeLocationId: newShift.assignedStaffHomeLocationId,
          assignedContractRoleId: newShift.assignedContractRoleId
        });
      }

      setWorkingRoster({ ...workingRoster, shifts });
    },
    [setWorkingRoster, workingRoster]
  );

  const removeShift = useCallback(
    (shiftIndex: number) => () => {
      if (!workingRoster) {
        throw Error('Working Roster should be set');
      }

      const shifts = [...workingRoster.shifts];
      shifts.splice(shiftIndex, 1);
      setWorkingRoster({ ...workingRoster, shifts });
    },
    [setWorkingRoster, workingRoster]
  );

  if (!workingRoster) {
    return <ErrorMessage message={strings.daily.roster.noRoster}></ErrorMessage>;
  } else {
    return (
      <Suspense>
        <div>
          {workingRoster?.shifts.map((shift, index) => (
            <RosterShift
              key={shift.id}
              shift={shift}
              removeShift={removeShift(index)}
              updateShift={updateShift(index)}
              selectContractRole={() => {}}
              readOnly={workingRoster.readOnly}
            />
          ))}
        </div>
      </Suspense>
    );
  }
};
